import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDSnackbar from "components/MDSnackbar";
import AppLoading from "verticales/AppLoading.tsx";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import { IconButton } from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import Tooltip from "@mui/material/Tooltip";
import fetchData from "verticales/FetchData";
import { mkConfig, generateCsv, download } from "export-to-csv";
import dayjs from "dayjs";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

// Componente para mostrar el estado con badge
const StatusBadge = ({ value, trueText, falseText }) => (
  <MDBadge
    badgeContent={value === "S" ? trueText : falseText}
    color={value === "S" ? "success" : "error"}
    variant="gradient"
    size="sm"
  />
);

StatusBadge.propTypes = {
  value: PropTypes.string.isRequired,
  trueText: PropTypes.string,
  falseText: PropTypes.string,
};

StatusBadge.defaultProps = {
  trueText: "Instalado",
  falseText: "No Instalado",
};

// Definición de PropTypes para las filas
const rowPropTypes = PropTypes.shape({
  original: PropTypes.shape({
    cuenta: PropTypes.string,
    instance_id: PropTypes.string,
    instance_name: PropTypes.string,
    producto: PropTypes.string,
    estado: PropTypes.string,
    aws_ec2_cpu_ram: PropTypes.string,
    aws_ec2_usuarios: PropTypes.string,
    aws_ec2_monitor_servicios: PropTypes.string,
    aws_ec2_signal_monitor_servicios: PropTypes.string,
  }),
});

const ViewEc2Agentes = ({ windowHeight }) => {
  const [loadingState, setLoadingState] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  // const [snackbarContent, setSnackbarContent] = useState("");
  const [snackbarColor, setSnackbarColor] = useState("info");

  const fetchEc2Agentes = async () => {
    try {
      setLoadingState(true);
      const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
      const context = "informes";

      const timestamp = dayjs().startOf("day").valueOf();
      const requestPayload = {
        action: "ec2agentes",
        time: timestamp,
      };

      const respData = await fetchData(url, context, requestPayload);

      if (respData && respData.rows) {
        const datos = respData.rows.map((row) => ({
          cuenta: row.cuenta,
          instance_id: row.instance_id,
          instance_name: row.instance_name,
          producto: row.producto,
          estado: row.estado,
          aws_ec2_cpu_ram: row.aws_ec2_cpu_ram,
          aws_ec2_usuarios: row.aws_ec2_usuarios,
          aws_ec2_monitor_servicios: row.aws_ec2_monitor_servicios,
          aws_ec2_signal_monitor_servicios: row.aws_ec2_signal_monitor_servicios,
        }));
        setTableData(datos);

        //setSnackbarContent("Datos de agentes cargados correctamente");
        setSnackbarColor("success");
        setOpenSnackbar(true);
      } else {
        throw new Error("Formato de respuesta inválido");
      }
    } catch (error) {
      console.error("Error al obtener agentes EC2:", error);
      setTableData([]);

      //setSnackbarContent("Error al cargar los datos de agentes");
      setSnackbarColor("error");
      setOpenSnackbar(true);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    fetchEc2Agentes();
  }, []);

  // Componente Celda para Estado
  const EstadoCell = ({ row }) => {
    return (
      <MDBadge
        badgeContent={row.original.estado}
        color={row.original.estado === "running" ? "success" : "error"}
        variant="gradient"
        size="sm"
      />
    );
  };

  EstadoCell.propTypes = {
    row: rowPropTypes.isRequired,
  };

  // Componente Celda genérico para StatusBadge
  const StatusCell = ({ row, field }) => {
    return <StatusBadge value={row.original[field]} />;
  };

  StatusCell.propTypes = {
    row: rowPropTypes.isRequired,
    field: PropTypes.string.isRequired,
  };

  const columns = useMemo(
    () => [
      { accessorKey: "cuenta", header: "Cuenta", size: 150 },
      { accessorKey: "instance_id", header: "Instance", size: 200 },
      { accessorKey: "instance_name", header: "Instance Name", size: 200 },
      { accessorKey: "producto", header: "Producto", size: 150 },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 100,
        Cell: EstadoCell,
      },
      {
        accessorKey: "aws_ec2_cpu_ram",
        header: "Cpu/Ram",
        size: 100,
        Cell: (props) => <StatusCell {...props} field="aws_ec2_cpu_ram" />,
      },
      {
        accessorKey: "aws_ec2_usuarios",
        header: "AgenteUsuarios",
        size: 100,
        Cell: (props) => <StatusCell {...props} field="aws_ec2_usuarios" />,
      },
      {
        accessorKey: "aws_ec2_monitor_servicios",
        header: "AgenteMonServicios",
        size: 100,
        Cell: (props) => <StatusCell {...props} field="aws_ec2_monitor_servicios" />,
      },
      {
        accessorKey: "aws_ec2_signal_monitor_servicios",
        header: "AgenteSignalMonitor",
        size: 100,
        Cell: (props) => <StatusCell {...props} field="aws_ec2_signal_monitor_servicios" />,
      },
    ],
    []
  );

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(tableData);
    download(csvConfig)(csv);

    // setSnackbarContent("Datos exportados correctamente");
    // setSnackbarColor("info");
    // setOpenSnackbar(false);
  };

  // const renderOpcRowsSid = (row) => (
  //   <Box sx={{ display: "flex", justifyContent: "center", width: "100%", padding: "0" }}>
  //     <Tooltip title="Ver análisis detallado">
  //       <IconButton onClick={() => console.log("Analizar:", row.original)}>
  //         <AnalyticsIcon />
  //       </IconButton>
  //     </Tooltip>
  //   </Box>
  // );

  // renderOpcRowsSid.propTypes = {
  //   row: PropTypes.shape({
  //     original: PropTypes.shape({
  //       cuenta: PropTypes.string,
  //       instance_id: PropTypes.string,
  //       instance_name: PropTypes.string,
  //       producto: PropTypes.string,
  //       estado: PropTypes.string,
  //       aws_ec2_cpu_ram: PropTypes.string,
  //       aws_ec2_usuarios: PropTypes.string,
  //       aws_ec2_monitor_servicios: PropTypes.string,
  //       aws_ec2_signal_monitor_servicios: PropTypes.string,
  //     }).isRequired,
  //   }).isRequired,
  // };

  return (
    <Box sx={{ marginTop: "1px" }}>
      {loadingState ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <AppLoading titleCustom={"Agentes EC2"} />
        </Box>
      ) : (
        <TableMaestros
          height={250}
          arrayColumns={columns}
          data={tableData}
          //renderOpcRows={renderOpcRowsSid}
          enableGrouping
          enableStickyHeader
          enableColumnResizing
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 300px)",
              minHeight: "400px",
              overflow: "auto",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid #e0e0e0",
              fontSize: 14,
              padding: "0.5rem",
            },
          }}
          muiTableHeadCellProps={{
            sx: (theme) => ({
              backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
              fontSize: 14,
              fontWeight: "bold",
            }),
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
              <MDButton
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                sx={{ fontSize: "12px", fontWeight: "normal" }}
              >
                Exportar Datos
              </MDButton>
              <MDButton onClick={fetchEc2Agentes} sx={{ fontSize: "12px", fontWeight: "normal" }}>
                Actualizar Datos
              </MDButton>
            </Box>
          )}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 },
          }}
        />
      )}

      {/* <MDSnackbar
        color={snackbarColor}
        icon={
          snackbarColor === "success" ? "check" : snackbarColor === "error" ? "warning" : "info"
        }
        title="Notificación"
        content={snackbarContent}
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        close={() => setOpenSnackbar(false)}
        autoHideDuration={5000}
      /> */}
    </Box>
  );
};

ViewEc2Agentes.propTypes = {
  windowHeight: PropTypes.number,
};

export default ViewEc2Agentes;
